import { keys, pick } from 'lodash';
import { extractParams } from '@zapier/url-utils';
import { readCookie, writeCookie } from '@zapier/cookies';

import TrackingConstants, { ClickIds } from './constants';
import { isBrowser } from './utils';

const TrackingCookies = {
  /* Returns the cookie name based on the ID, e.g. __fbclid */
  getClickIdCookieName(id: ClickIds) {
    return `__${id}`;
  },

  /* Retrieves a cookie for a single click ID query parameter, e.g. fbclid, gclid, msclkid */
  retrieveClickId(id: ClickIds) {
    const cookie = readCookie(this.getClickIdCookieName(id));
    if (cookie) {
      try {
        return atob(cookie);
      } catch (e) {
        console.error(`Error retrieving ${id}: ${e}`);
      }
    }
    return null;
  },

  /* Stores a cookie for a single click ID query parameter, e.g. fbclid, gclid, msclkid */
  storeClickId(id: ClickIds, href: string = window.location.href) {
    if (isBrowser()) {
      const params = extractParams(href);
      const param = params[id];
      if (param) {
        writeCookie(this.getClickIdCookieName(id), btoa(param), {
          category: 'marketing',
          maxAge: TrackingConstants.CLICK_ID_COOKIE_EXPIRATION,
        });
      }
    }
  },

  /* Iterates through all ClickIds values and fetches cookies for each, e.g. fbclid, gclid, msclkid */
  retrieveAllClickIds() {
    Object.values(ClickIds).forEach((id: ClickIds) => {
      this.retrieveClickId(id);
    });
  },

  /* Iterates through all ClickIds values and stores cookies for each, e.g. fbclid, gclid, msclkid */
  storeAllClickIds() {
    Object.values(ClickIds).forEach((id: ClickIds) => {
      this.storeClickId(id);
    });
  },

  retrieveUtms() {
    if (isBrowser()) {
      const utmCookie = readCookie(TrackingConstants.UTM_COOKIE_NAME);

      if (utmCookie) {
        try {
          return JSON.parse(utmCookie) as {
            utm_campaign: string;
            utm_source: string;
            utm_content: string;
            utm_medium: string;
          };
        } catch (e) {
          // If we fail to parse the cookie, then we will resolve everything
          // as `undefined`.
        }
      }
    }

    return {
      utm_campaign: undefined,
      utm_source: undefined,
      utm_content: undefined,
      utm_medium: undefined,
    };
  },

  storeUtms(url?: string) {
    if (isBrowser()) {
      try {
        const href = url || window.location.href;
        const params = extractParams(href);
        const utmParams = pick(params, TrackingConstants.UTM_PARAM_NAMES);

        if (keys(utmParams).length > 0) {
          writeCookie(
            TrackingConstants.UTM_COOKIE_NAME,
            JSON.stringify(utmParams),
            {
              category: 'marketing',
              maxAge: TrackingConstants.UTM_COOKIE_EXPIRATION,
            }
          );
        }

        return undefined;
      } catch (e) {
        // If we fail to set the cookie, then we will resolve `undefined`.
      }
    }

    return undefined;
  },
};
export default TrackingCookies;
